import { useEffect } from 'react';
import { isAPIError } from 'shared/api/APIError';
import { saveCarrierGuid } from 'shared/auth/AuthToken';
import {
  getMobileAppToken,
  MobileAppBridge,
} from 'shared/data/MobileAppBridge';
import { identifyAnalytics } from 'shared/helpers/Analytics';
import { useOnlineStatus } from 'shared/helpers/BrowserHelpers';
import { useCustomerSupport } from 'shared/helpers/CustomerSupport';
import { identifyUser, logError } from 'shared/helpers/Logger';
import { SplashScreen } from 'shared/layout/SplashScreen';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { trackLoadboardEvent } from './core/LoadboardAnalytics';
import { LoadboardMobileRoutes } from './LoadboardMobileApplication';
import { LoadboardMobileErrorDialog } from './webview/LoadboardMobileErrorDialog';

export function LoadboardWebviewApp() {
  const { hideLauncher } = useCustomerSupport();
  const onlineStatus = useOnlineStatus();
  const { data: settings, error: settingsError } = useCarrierSettings({
    enableCache: true,
  });

  useEffect(() => {
    if (settings?.carrier.guid) {
      saveCarrierGuid(settings.carrier.guid);
    }

    if (settings) {
      const bridge = window.MobileLoadboardInterface;
      identifyUser({
        id: settings.user.guid,
        carrier_guid: settings.carrier.guid,
        carrier_name: settings.carrier.name,
        company_type: settings.company_type,
        driverGuid: bridge?.getDriverGuid?.(),
        deviceGuid: bridge?.getDeviceGuid?.(),
      });
      identifyAnalytics({
        guid: settings.user.guid,
        hash: settings.user.user_hash,
        accountType: settings.company_type,
        company: {
          id: settings.carrier.guid,
          guid: settings.carrier.guid,
          name: settings.carrier.name,
          is_super: settings.carrier.verification_status === 'approved',
          superpay_onboarding_status: settings.superpay.verification,
          is_fmcsa_phone_verified: settings.is_fmcsa_phone_verified,
        },
      });
      trackLoadboardEvent({ name: 'SLB: Opened Loadboard' });
    }

    hideLauncher();
  }, [settings, hideLauncher]);

  useEffect(() => {
    if (settingsError) {
      logError(settingsError, 'LoadboardMobileApp', {
        extraInfo: {
          hasToken: !!getMobileAppToken(),
          hasBridge: MobileAppBridge.isInjected(),
          driverGuid: MobileAppBridge.getDriverGuid(),
          deviceGuid: MobileAppBridge.getDeviceGuid(),
          carrierGuid: MobileAppBridge.getCarrierGuid(),
        },
      });
    }
  }, [settingsError]);

  if (onlineStatus === 'offline') {
    return <LoadboardMobileErrorDialog message="No Internet connection" />;
  }

  if (settingsError) {
    return (
      <LoadboardMobileErrorDialog
        message={
          isAPIError(settingsError)
            ? settingsError.message
            : 'Failed to load current user'
        }
      />
    );
  }

  if (!settings?.user) {
    return <SplashScreen />;
  }

  return <LoadboardMobileRoutes />;
}
